import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Plus, Search } from "lucide-react";

import { Alert } from "@/components/Alert";
import { DataTable } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { FormSelect } from "@/components/FormSelect";
import { ListToolbar } from "@/components/ListToolbar";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Pagination } from "@/components/Pagination";
import { Toast } from "@/components/Toast";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/auth/useAuth";
import { useResourceList } from "@/hooks/useResourceList";
import { DIRECTORY_WRITE_ROLES } from "./statuses";

export function DirectoryListPage({
  title,
  description,
  resource,
  statuses,
  columns,
  basePath,
  addLabel,
  searchPlaceholder,
  emptyMessage,
  emptyIcon,
  emptyTone = "rose",
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole } = useAuth();
  const canEdit = hasRole(...DIRECTORY_WRITE_ROLES);

  const [toastMessage, setToastMessage] = useState(location.state?.toastMessage || null);

  useEffect(() => {
    if (location.state?.toastMessage) {
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const { rows, count, isLoading, error, page, setPage, search, setSearch, status, setStatus } =
    useResourceList(resource);

  if (isLoading && rows.length === 0) {
    return <PageSkeleton />;
  }

  const statusOptions = [{ value: "", label: "All statuses" }, ...statuses];
  const isFiltered = Boolean(search || status);

  return (
    <PageShell>
      <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      <PageHeader
        title={title}
        description={description}
        actions={
          canEdit ? (
            <Button asChild>
              <Link to={`${basePath}/new`}>
                <Plus />
                {addLabel}
              </Link>
            </Button>
          ) : null
        }
      />

      <PageBody>
        <ListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={searchPlaceholder}
        >
          <div className="w-full max-w-xs">
            <FormSelect
              id="directory-status-filter"
              value={status}
              onValueChange={setStatus}
              options={statusOptions}
              placeholder="All statuses"
            />
          </div>
        </ListToolbar>

        <Alert>{error}</Alert>

        {rows.length === 0 ? (
          <EmptyState
            icon={isFiltered ? Search : emptyIcon}
            tone={isFiltered ? "slate" : emptyTone}
            title={isFiltered ? "No matches for that search." : emptyMessage}
            message={
              isFiltered
                ? "Try adjusting your search or status filter."
                : undefined
            }
            action={
              !isFiltered && canEdit ? (
                <Button asChild>
                  <Link to={`${basePath}/new`}>
                    <Plus />
                    {addLabel}
                  </Link>
                </Button>
              ) : null
            }
          />
        ) : (
          <div aria-busy={isLoading}>
            <DataTable
              columns={columns}
              rows={rows}
              getRowKey={(row) => row.id}
              onRowClick={(row) => navigate(`${basePath}/${row.id}`)}
            />
          </div>
        )}

        <Pagination page={page} count={count} onChange={setPage} />
      </PageBody>
    </PageShell>
  );
}
